import Vue from 'vue';
import Router from 'vue-router';
import store from './store';

Vue.use(Router);

function requireAuth(to, from, next) {
  // if not Authenticated: reroute to login
  if (!store.getters['auth/isAuthenticated']) {
    next({
      path: '/login',
      query: { redirect: to.fullPath },
    });
  } else {
    next();
  }
}
function loginGuard(to, from, next) {
  // if authenticated, reroute to account
  if (store.getters['auth/isAuthenticated']) {
    next({ name: 'profile' });
  } else {
    next();
  }
}
/*
function checkoutCompleteGuard(to, from, next) {
  if (store.getters['cart/successfulCheckout']) {
    next();
  } else {
    next('/');
  }
}
*/

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/comingsoon',
      name: 'coming-soon',
      component: () => import('./views/ComingSoon.vue'),
    },
    {
      path: '/',
      name: 'home',
      component: () => import('./views/Home.vue'),
    },
    // AUTHENTICATION
    {
      path: '/login',
      name: 'login',
      component: () => import('./views/auth/Login.vue'),
      beforeEnter: loginGuard,
    },
    {
      path: '/logout',
      name: 'logout',
      beforeEnter: (to, from, next) => {
        if (store.getters['auth/isAuthenticated']) {
          store.dispatch('auth/logout');
          store.dispatch('app/globalMessage', 'You have been logged out');
        }
        next({ name: 'home' });
      },
    },
    {
      path: '/register',
      name: 'create-account',
      component: () => import('./views/auth/CreateAccount.vue'),
      beforeEnter: loginGuard,
    },
    {
      path: '/login/:token',
      name: 'verify-email',
      component: () => import('./views/auth/Login.vue'),
    },
    {
      path: '/reset-password/:token',
      name: 'reset-password',
      component: () => import('./views/auth/ResetPassword.vue'),
    },
    // HATS
    {
      path: '/hats',
      name: 'hat-catalog',
      component: () => import('./views/HatCatalog.vue'),
    },
    {
      path: '/hats/:slug',
      name: 'hat-product',
      component: () => import('./views/HatProduct.vue'),
    },
    // PATCHES
    {
      path: '/patches/catalog',
      name: 'patch-catalog',
      component: () => import('./views/PatchCatalog.vue'),
      props: {
        tag: '',
      },
    },
    {
      path: '/patches/submissions',
      name: 'patch-submissions',
      component: () => import('./views/PatchSubmissions.vue'),
    },
    { // for both catalog and submissions
      path: '/patches/:patchGroup/:id',
      name: 'patch-product',
      component: () => import('./views/PatchProduct.vue'),
    },
    // CUSTOM
    {
      path: '/custom',
      name: 'custom-start',
      component: () => import('./views/custom/CustomStart.vue'),
    },
    {
      path: '/custom/create',
      name: 'create-custom',
      component: () => import('./views/custom/CreateCustom.vue'),
    },
    // SUBMIT
    {
      path: '/submit',
      name: 'submit-start',
      component: () => import('./views/submit/SubmitStart.vue'),
    },
    {
      path: '/submit/create',
      name: 'submit-design',
      component: () => import('./views/submit/SubmitDesign.vue'),
      beforeEnter: requireAuth,
    },
    // ARTICLES
    {
      path: '/articles',
      name: 'articles',
      component: () => import('./views/articles/AllArticles.vue'),
    },
    {
      path: '/articles/:slug',
      name: 'article',
      component: () => import('./views/articles/SingleArticle.vue'),
    },
    // CART
    {
      path: '/cart',
      name: 'cart',
      component: () => import('./views/Cart.vue'),
    },
    // CHECKOUT
    {
      path: '/checkout',
      name: 'checkout',
      component: () => import('./views/Checkout.vue'),
      props: {
        fromCart: false,
      },
      beforeEnter: (to, from, next) => {
        // can only access checkout from cart
        if (!to.params.fromCart) {
          next({ name: 'cart' });
        }
        next();
      },
    },
    {
      // Order Confirmation
      path: '/checkout/complete',
      name: 'order-confirmation',
      component: () => import('./views/OrderConfirmation.vue'),
      props: {
        fromCheckout: false,
      },
      beforeEnter: (to, from, next) => {
        // can only access from checkout
        if (!to.params.fromCheckout) {
          next('/');
        }
        next();
      },
    },
    // INFO HUB
    {
      path: '/info',
      component: () => import('./views/InfoHub.vue'),
      children: [
        {
          path: 'terms-and-conditions',
          name: 'terms-and-conditions',
          component: () => import('./components/docs/TermsAndConditions.vue'),
        },
        {
          path: 'privacy-policy',
          name: 'privacy-policy',
          component: () => import('./components/docs/PrivacyPolicy.vue'),
        },
        {
          path: 'returns-policy',
          name: 'returns-policy',
          component: () => import('./components/docs/ReturnsPolicy.vue'),
        },
        {
          path: 'artist-agreement',
          name: 'artist-agreement',
          component: () => import('./components/docs/ArtistAgreement.vue'),
        },
        {
          path: 'patch-care',
          name: 'patch-care',
          component: () => import('./components/docs/PatchCare.vue'),
        },
        // redirect info root or any other info route
        {
          path: '',
          redirect: '/',
        },
      ],
    },
    // ACCOUNT
    {
      path: '/account',
      component: () => import('./views/account/AccountHome.vue'),
      beforeEnter: requireAuth,
      children: [
        {
          path: 'profile',
          name: 'profile',
          component: () => import('./views/account/Profile.vue'),
          beforeEnter: requireAuth,
        },
        {
          path: 'order-history',
          name: 'order-history',
          component: () => import('./views/account/OrderHistory.vue'),
          beforeEnter: requireAuth,
        },
        {
          path: 'my-patches',
          name: 'my-patches',
          component: () => import('./views/account/MyPatches.vue'),
          beforeEnter: requireAuth,
        },
        /*
        {
          path: 'settings',
          name: 'settings',
          component: () => import('./views/account/Settings.vue'),
          beforeEnter: requireAuth,
        },
        */
        {
          path: '/',
          redirect: 'profile',
        },
      ],
    },
    // ERROR ROUTES
    {
      path: '/404',
      name: '404',
      component: () => import('./views/errors/404.vue'),
    },
    {
      path: '*',
      redirect: '/404',
    },
  ],
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    return { x: 0, y: 0 };
  },
});

// coming soon access check
router.beforeEach((to, from, next) => {
  // if going to coming soon
  if (to.name === 'coming-soon') {
    if (store.getters['app/isComingSoon']) {
      next();
    } else {
      next('/');
    }
  } else {
    // if coming soon
    if (store.getters['app/isComingSoon']) {
      // if has access
      if (store.getters['auth/hasComingSoonAccess']) {
        next();
      } else {
        next('/comingsoon');
      }
    }
    next();
  }
});

export default router;
